import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Check, Crown, Sparkles, FlaskConical } from "lucide-react";

const PLANS = {
  monthly: { lookup: "creator_monthly", price: "$6.99", per: "/month" },
  yearly: { lookup: "creator_yearly", price: "$69", per: "/year" },
};
const FREE = ["Text, image & remix generation", "5s and 10s clips", "Instant preview & download"];
const PRO = ["Everything in Free", "Unlimited saved gallery", "Full generation history", "Priority rendering queue", "Creator badge"];

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [cycle, setCycle] = useState("monthly");
  const [stripeMode, setStripeMode] = useState("live");
  useEffect(() => {
    api.get("/config").then((r) => setStripeMode(r.data.stripe_mode)).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const subscribed = user && user !== false && user.subscription_status === "active";

  const subscribe = async () => {
    if (!user || user === false) { nav("/auth?mode=register"); return; }
    setLoading(true);
    try {
      await refreshUser();
      const { data } = await api.post("/payments/checkout", {
        lookup_key: PLANS[cycle].lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-14 grain">
      <div className="text-center mb-10">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Simple, creator-first pricing</h1>
        <p className="text-muted-foreground mt-3">Create for free. Upgrade when you want to keep your work forever.</p>
      </div>

      {stripeMode === "test" && (
        <div data-testid="test-mode-banner" className="max-w-xl mx-auto mb-8 glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-primary/30">
          <FlaskConical className="w-4 h-4 text-primary shrink-0" />
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Test mode:</span> payments aren't live yet — use card 4242&nbsp;4242&nbsp;4242&nbsp;4242 to try checkout. Real cards won't be charged until Stripe is activated.
          </p>
        </div>
      )}

      <div className="flex justify-center mb-10">
        <div className="glass rounded-full p-1 flex">
          {["monthly", "yearly"].map((c) => (
            <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
              className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${
                cycle === c ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}>
              {c}{c === "yearly" && <span className="ml-1 text-xs opacity-80">· save 18%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8">
          <h3 className="font-display text-2xl font-bold">Free</h3>
          <p className="text-4xl font-display font-extrabold mt-4">$0</p>
          <p className="text-muted-foreground text-sm mb-6">Explore the full engine.</p>
          <ul className="space-y-3 mb-8">
            {FREE.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-primary" />{f}</li>)}
          </ul>
          <Button onClick={() => nav("/studio")} variant="outline" className="w-full rounded-xl border-border font-semibold">Start creating</Button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-3xl p-8 relative glass glow-pink border border-accent/30">
          <span className="absolute top-6 right-6 flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-accent/15 text-accent">
            <Crown className="w-3.5 h-3.5" /> Creator
          </span>
          <h3 className="font-display text-2xl font-bold">Creator</h3>
          <p className="text-4xl font-display font-extrabold mt-4">{PLANS[cycle].price}<span className="text-base font-medium text-muted-foreground">{PLANS[cycle].per}</span></p>
          <p className="text-muted-foreground text-sm mb-6">For serious motion makers.</p>
          <ul className="space-y-3 mb-8">
            {PRO.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-accent" />{f}</li>)}
          </ul>
          {subscribed ? (
            <Button disabled className="w-full rounded-xl font-semibold" data-testid="already-pro">You're a Creator ✓</Button>
          ) : (
            <Button data-testid="subscribe-btn" onClick={subscribe} disabled={loading} className="w-full rounded-xl font-semibold">
              <Sparkles className="w-4 h-4 mr-2" /> {loading ? "Redirecting…" : "Upgrade to Creator"}
            </Button>
          )}
        </motion.div>
      </div>
    </div>
  );
}
